import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Lock, Loader2, Menu, Users, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePortalBySlug } from '@/hooks/portal';
import { useAgentPageByUrlName } from '@/hooks/agent/useAgentPage';
import { PortalGenie, PortalLayoutConfig } from '@/types/portal/portal';
import { DEFAULT_BRANDING, GenieBranding } from '@/types/ui/branding';
import { PortalService } from '@/services/database/PortalService';
import { GenieAvatar } from '@/components/GenieAvatar';
import LiveAgentPage from '@/pages/agent-interfaces/LiveAgentPage';

const PublicPortalPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: portal, isLoading, error } = usePortalBySlug(slug || '');
  const [passwordInput, setPasswordInput] = useState('');
  const [isPasswordVerified, setIsPasswordVerified] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Selected genie state for embedded experience
  const [selectedGenie, setSelectedGenie] = useState<PortalGenie | null>(null);
  const selectedUrlName = selectedGenie?.agent?.page?.url_name;
  const { data: selectedAgentPageData, isLoading: loadingAgentPage } = useAgentPageByUrlName(selectedUrlName);

  // Resolved branding state (portal branding → account branding → default)
  const [resolvedBranding, setResolvedBranding] = useState<GenieBranding>(DEFAULT_BRANDING);

  // Mobile drawer state
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Track view on load
  useEffect(() => {
    if (portal?.id && !portal.password_protected) {
      PortalService.incrementViewCount(portal.id);
    }
  }, [portal?.id, portal?.password_protected]);

  // Resolve branding with fallback chain
  useEffect(() => {
    const resolveBranding = async () => {
      if (!portal) {
        return;
      }

      // If portal has custom branding, use it directly
      if (portal.branding) {
        setResolvedBranding(portal.branding);
        return;
      }

      // Otherwise, try to get account branding
      try {
        const accountBranding = await PortalService.getAccountBrandingForPortal(portal.user_id);
        if (accountBranding) {
          setResolvedBranding(accountBranding);
        } else {
          setResolvedBranding(DEFAULT_BRANDING);
        }
      } catch (err) {
        console.error('Error fetching account branding:', err);
        setResolvedBranding(DEFAULT_BRANDING);
      }
    };

    resolveBranding();
  }, [portal]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading portal...</p>
        </div>
      </div>
    );
  }

  if (error || !portal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center max-w-md px-4">
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
            <Users className="w-10 h-10 text-gray-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Portal Not Found</h1>
          <p className="text-gray-600">
            The portal you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  // Password protection check
  if (portal.password_protected && !isPasswordVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center max-w-md px-4">
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-amber-50 to-amber-100 rounded-full flex items-center justify-center">
            <Lock className="w-10 h-10 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Password Protected</h1>
          <p className="text-gray-600 mb-6">
            This portal requires a password to access.
          </p>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              // TODO: Implement actual password verification via Edge Function
              setPasswordError('Password verification not yet implemented');
            }}
            className="space-y-4"
          >
            <Input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="Enter password"
              className="text-center"
            />
            {passwordError && (
              <p className="text-sm text-red-600">{passwordError}</p>
            )}
            <Button type="submit" className="w-full">
              Access Portal
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Use resolved branding (portal branding → account branding → default)
  const branding = resolvedBranding;
  const primaryColor = branding.primaryColor || DEFAULT_BRANDING.primaryColor;
  const secondaryColor = branding.secondaryColor;
  const gradientEnabled = branding.gradientEnabled;
  const gradientAngle = branding.gradientAngle || 135;
  const layoutConfig: PortalLayoutConfig = portal.layout_config || { columns: 3 };

  const getGridCols = () => {
    switch (layoutConfig.columns) {
      case 2:
        return 'grid-cols-1 sm:grid-cols-2';
      case 4:
        return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
      default:
        return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
    }
  };

  const handleGenieClick = (portalGenie: PortalGenie) => {
    const urlName = portalGenie.agent?.page?.url_name;
    if (urlName) {
      setSelectedGenie(portalGenie);
    }
  };

  const handleBackToPortal = () => {
    setSelectedGenie(null);
  };

  // Shared genie list component for sidebar and mobile drawer
  const GenieList = ({ onGenieSelect }: { onGenieSelect?: () => void }) => (
    <nav className="space-y-1 px-2">
      {portal.genies?.map((genie) => {
        const isSelected = genie.id === selectedGenie?.id;
        const hasUrlName = !!genie.agent?.page?.url_name;
        const genieBrand = genie.agent?.branding?.primaryColor || primaryColor;

        return (
          <button
            key={genie.id}
            onClick={() => {
              if (hasUrlName) {
                handleGenieClick(genie);
                onGenieSelect?.();
              }
            }}
            disabled={!hasUrlName}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left ${
              isSelected
                ? 'bg-gray-100 shadow-sm'
                : 'hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
            }`}
            style={{
              borderLeft: isSelected ? `3px solid ${genieBrand}` : '3px solid transparent',
            }}
          >
            <div className="flex-shrink-0">
              <GenieAvatar
                color={genieBrand}
                secondaryColor={genie.agent?.branding?.secondaryColor}
                gradientEnabled={genie.agent?.branding?.gradientEnabled}
                gradientAngle={genie.agent?.branding?.gradientAngle}
                avatarBackgroundOpacity={genie.agent?.branding?.avatarBackgroundOpacity}
                customAvatarUrl={genie.custom_avatar_url || genie.agent?.branding?.logoUrl || genie.agent?.branding?.customAvatarUrl}
                size={36}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium truncate ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                {genie.display_name || genie.agent?.name}
              </p>
              {genie.category_label && (
                <p className="text-xs text-gray-400 truncate">
                  {genie.category_label}
                </p>
              )}
            </div>
            {genie.is_featured && (
              <div
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: genieBrand }}
                title="Featured"
              />
            )}
          </button>
        );
      })}
    </nav>
  );

  // If a genie is selected, show the sidebar layout with embedded live agent
  if (selectedGenie) {
    return (
      <div className="min-h-screen flex flex-col md:flex-row bg-white">
        {/* Mobile Header */}
        <header
          className="md:hidden sticky top-0 z-50 flex items-center justify-between px-4 py-3 border-b border-white/20"
          style={{
            background: gradientEnabled && secondaryColor
              ? `linear-gradient(${gradientAngle}deg, ${primaryColor}, ${secondaryColor})`
              : primaryColor,
          }}
        >
          <button
            onClick={() => setMobileDrawerOpen(true)}
            className="p-2 -ml-2 text-white/90 hover:text-white"
          >
            <Menu className="w-5 h-5" />
          </button>
          <span className="text-white font-semibold text-sm truncate flex-1 text-center px-2">
            {selectedGenie.display_name || selectedGenie.agent?.name}
          </span>
          <button
            onClick={handleBackToPortal}
            className="text-white/80 hover:text-white text-xs"
          >
            ← Back
          </button>
        </header>

        {/* Mobile Drawer Overlay */}
        {mobileDrawerOpen && (
          <div className="md:hidden fixed inset-0 z-50">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setMobileDrawerOpen(false)}
            />
            {/* Drawer */}
            <aside
              className="absolute left-0 top-0 bottom-0 w-72 flex flex-col bg-white shadow-xl"
              style={{
                animation: 'slideIn 0.2s ease-out',
              }}
            >
              {/* Drawer Header */}
              <div
                className="p-4 flex items-center justify-between"
                style={{
                  background: gradientEnabled && secondaryColor
                    ? `linear-gradient(${gradientAngle}deg, ${primaryColor}, ${secondaryColor})`
                    : primaryColor,
                }}
              >
                <button
                  onClick={() => {
                    setMobileDrawerOpen(false);
                    handleBackToPortal();
                  }}
                  className="flex-1 text-left group"
                >
                  {branding.logoUrl ? (
                    <img
                      src={branding.logoUrl}
                      alt={portal.name}
                      className="h-7 object-contain mb-1 brightness-0 invert opacity-90"
                    />
                  ) : null}
                  <h2 className="text-white font-semibold text-sm truncate">
                    {portal.name}
                  </h2>
                  <p className="text-white/70 text-xs">← Back to all genies</p>
                </button>
                <button
                  onClick={() => setMobileDrawerOpen(false)}
                  className="p-2 -mr-2 text-white/80 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {/* Drawer Genie List */}
              <div className="flex-1 overflow-y-auto py-2">
                <GenieList onGenieSelect={() => setMobileDrawerOpen(false)} />
              </div>
            </aside>
          </div>
        )}

        {/* Desktop Sidebar - Hidden on mobile */}
        <aside
          className="hidden md:flex w-64 flex-shrink-0 border-r border-gray-200 flex-col h-screen sticky top-0"
          style={{
            background: 'linear-gradient(180deg, #FAFAFA 0%, #FFFFFF 100%)',
          }}
        >
          {/* Portal Header in Sidebar */}
          <div
            className="p-4 border-b border-gray-200"
            style={{
              background: gradientEnabled && secondaryColor
                ? `linear-gradient(${gradientAngle}deg, ${primaryColor}, ${secondaryColor})`
                : primaryColor,
            }}
          >
            <button
              onClick={handleBackToPortal}
              className="w-full text-left group"
            >
              {branding.logoUrl ? (
                <img
                  src={branding.logoUrl}
                  alt={portal.name}
                  className="h-8 object-contain mb-2 brightness-0 invert opacity-90 group-hover:opacity-100 transition-opacity"
                />
              ) : null}
              <h2 className="text-white font-semibold text-sm truncate group-hover:underline">
                {portal.name}
              </h2>
              <p className="text-white/70 text-xs mt-0.5">← Back to all genies</p>
            </button>
          </div>

          {/* Genie List */}
          <div className="flex-1 overflow-y-auto py-2">
            <GenieList />
          </div>
        </aside>

        {/* Main Content - Live Agent */}
        <main className="flex-1 min-h-screen md:min-h-0">
          {loadingAgentPage ? (
            <div className="flex items-center justify-center h-full min-h-[400px]">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">Loading {selectedGenie.display_name || selectedGenie.agent?.name}...</p>
              </div>
            </div>
          ) : selectedAgentPageData ? (
            <LiveAgentPage agentPageData={selectedAgentPageData} portalMode />
          ) : (
            <div className="flex items-center justify-center h-full min-h-[400px]">
              <div className="text-center">
                <p className="text-gray-500">Unable to load genie. Please try again.</p>
                <Button variant="outline" onClick={handleBackToPortal} className="mt-4">
                  Back to Portal
                </Button>
              </div>
            </div>
          )}
        </main>

        {/* CSS for drawer animation */}
        <style>{`
          @keyframes slideIn {
            from { transform: translateX(-100%); }
            to { transform: translateX(0); }
          }
        `}</style>
      </div>
    );
  }

  // Default portal view with genie grid
  return (
    <div className="min-h-screen bg-white">
      {/* Header with wavy bottom */}
      <header className="relative">
        <div
          className="relative pb-8 md:pb-10"
          style={{
            background: gradientEnabled && secondaryColor
              ? `linear-gradient(${gradientAngle}deg, ${primaryColor}, ${secondaryColor})`
              : primaryColor,
          }}
        >
          <div className="relative max-w-6xl mx-auto px-4 py-8 md:py-10 text-center">
            {branding.logoUrl && (
              <img
                src={branding.logoUrl}
                alt={portal.name}
                className="h-12 md:h-14 mx-auto mb-3 object-contain drop-shadow-lg"
              />
            )}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 drop-shadow-sm">
              {portal.name}
            </h1>
            {portal.description && (
              <p className="text-white/90 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
                {portal.description}
              </p>
            )}
            {/* Welcome Message - now inside header */}
            {portal.welcome_message && (
              <p className="text-white/80 text-base md:text-lg max-w-3xl mx-auto leading-relaxed mt-3 pt-3 border-t border-white/20">
                {portal.welcome_message}
              </p>
            )}
          </div>
        </div>
        {/* Wavy bottom edge - tighter */}
        <svg
          className="absolute bottom-0 left-0 w-full"
          viewBox="0 0 1440 40"
          preserveAspectRatio="none"
          style={{ height: '40px' }}
        >
          <path
            d="M0,20 C360,40 720,0 1080,20 C1260,30 1380,30 1440,20 L1440,40 L0,40 Z"
            fill="#FFFFFF"
          />
        </svg>
      </header>

      {/* Genies Grid - closer to header */}
      <main className="max-w-6xl mx-auto px-4 py-4 md:py-6">
        {portal.genies && portal.genies.length > 0 ? (
          <div className={`grid ${getGridCols()} gap-6 md:gap-8`}>
            {portal.genies.map((portalGenie) => (
              <GenieCard
                key={portalGenie.id}
                portalGenie={portalGenie}
                primaryColor={primaryColor}
                showDescription={layoutConfig.showDescriptions !== false}
                onClick={() => handleGenieClick(portalGenie)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
              <Users className="w-10 h-10 text-gray-400" />
            </div>
            <p className="text-gray-600 text-lg">No genies have been added to this portal yet.</p>
          </div>
        )}
      </main>
    </div>
  );
};

// Genie Card Component - Minimal, clean design focused on avatar and name
interface GenieCardProps {
  portalGenie: PortalGenie;
  primaryColor: string;
  showDescription: boolean;
  onClick: () => void;
}

const GenieCard = ({ portalGenie, primaryColor, showDescription, onClick }: GenieCardProps) => {
  const name = portalGenie.display_name || portalGenie.agent?.name || 'Genie';
  const description = portalGenie.display_description || portalGenie.agent?.description;
  const agentBranding = portalGenie.agent?.branding;
  const brand = agentBranding?.primaryColor || primaryColor;

  // Check if genie has a valid url_name to navigate to
  const hasUrlName = !!portalGenie.agent?.page?.url_name;

  return (
    <button
      onClick={onClick}
      disabled={!hasUrlName}
      className="group relative p-4 text-center transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
    >
      {/* Featured indicator - subtle dot */}
      {portalGenie.is_featured && (
        <div
          className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full animate-pulse"
          style={{ backgroundColor: brand }}
          title="Featured"
        />
      )}

      {/* Avatar - the main focus */}
      <div className="flex justify-center mb-4">
        <div className="transform transition-all duration-300 group-hover:scale-110 group-hover:drop-shadow-lg">
          <GenieAvatar
            color={brand}
            secondaryColor={agentBranding?.secondaryColor}
            gradientEnabled={agentBranding?.gradientEnabled}
            gradientAngle={agentBranding?.gradientAngle}
            avatarBackgroundOpacity={agentBranding?.avatarBackgroundOpacity}
            customAvatarUrl={portalGenie.custom_avatar_url || agentBranding?.logoUrl || agentBranding?.customAvatarUrl}
            size={100}
          />
        </div>
      </div>

      {/* Name - clean and simple */}
      <h3 className="font-semibold text-gray-900 text-base mb-1 transition-colors group-hover:text-gray-700">
        {name}
      </h3>

      {/* Category label - subtle */}
      {portalGenie.category_label && (
        <span className="text-xs text-gray-400 font-medium">
          {portalGenie.category_label}
        </span>
      )}

      {/* Description - only on hover or if enabled */}
      {showDescription && description && (
        <p className="text-xs text-gray-500 mt-2 line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          {description}
        </p>
      )}
    </button>
  );
};

export default PublicPortalPage;
